import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DELIVERY_STATUS_LABELS,
  DELIVERY_STATUSES,
  DELIVERY_STATUS_SUCCESORS,
  PROCESSING_STAGE_LABELS,
  TRANSACTION_STATUS_LABELS,
  TRANSACTION_STATUSES,
  availableTransitions,
  centsToPesos,
  type DeliveryStatus,
  type StatusTransitionRule,
  type TransactionStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Modal, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';
import { DeliveryStatusBadge, StatusBadge, type TransactionDto } from './TransactionsPage';
import { type ProcessingRecordDto } from '../ProcessingQueuePage';

export interface StatusHistoryEntryDto {
  id: string;
  fromStatus: TransactionStatus | null;
  toStatus: TransactionStatus;
  actorName: string;
  actorRole: string;
  note: string | null;
  createdAt: string;
}

/** Phase 2D - immutable release snapshot written at READY -> RELEASED. */
export interface ReleaseRecordDto {
  id: string;
  method: 'PICKUP' | 'DELIVERY';
  customerName: string;
  itemCount: number;
  paymentStatus: 'UNPAID' | 'PARTIAL' | 'PAID' | 'OVERPAID';
  amountPaidCents: number;
  totalCents: number;
  releasedByName: string;
  releasedByRole: string;
  notes: string | null;
  createdAt: string;
}

/** Phase 2F - append-only delivery record (one row per delivery action). */
export interface DeliveryHandoffDto {
  id: string;
  fromStatus: DeliveryStatus;
  toStatus: DeliveryStatus;
  actorName: string;
  actorRole: string;
  assignee: string | null;
  deliveryMethod: string | null;
  itemCountAtHandoff: number | null;
  notes: string | null;
  createdAt: string;
}

/** Detail payload: list DTO + the complete status transition timeline. */
export interface TransactionDetailDto extends TransactionDto {
  statusHistory: StatusHistoryEntryDto[];
  /** Phase 2C - append-only processing steps with item accountability. */
  processing: ProcessingRecordDto[];
  /** Phase 2D - release snapshot; null while the transaction is not released. */
  release: ReleaseRecordDto | null;
  /** Phase 2F - delivery records; empty for pick-up transactions. */
  deliveryHandoffs: DeliveryHandoffDto[];
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Admin',
  WASHING_EMPLOYEE: 'Washing employee',
  FOLDING_EMPLOYEE: 'Folding employee',
  SYSTEM: 'System',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Phase 2F - button label per delivery step (state labels stay canonical). */
const DELIVERY_STEP_ACTIONS: Partial<Record<DeliveryStatus, string>> = {
  ASSIGNED: 'Assign delivery',
  OUT_FOR_DELIVERY: 'Start delivery',
  DELIVERED: 'Mark delivered',
  DELIVERY_DONE: 'Close delivery',
};

/* ---------------- detail modal: breakdown + workflow + history ---------------- */

export function MonitoringDetailModal({
  tx,
  onClose,
  onChanged,
}: {
  tx: TransactionDetailDto;
  onClose: () => void;
  onChanged: (next: TransactionDetailDto) => void;
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 2D - release always goes through this one deliberate confirmation.
  const [confirmingRelease, setConfirmingRelease] = useState(false);
  const [releaseNote, setReleaseNote] = useState('');
  // Phase 2F - delivery step being confirmed (null = panel closed).
  const [deliveryTarget, setDeliveryTarget] = useState<DeliveryStatus | null>(null);
  const [assignee, setAssignee] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'DRIVER' | 'OTHER'>('DRIVER');
  const [handoffCount, setHandoffCount] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');

  // Display-only hint; the server re-authorizes every transition.
  const actions: StatusTransitionRule[] = useMemo(
    () => (user ? availableTransitions(user.role, tx.status) : []),
    [user, tx.status],
  );

  // Phase 2F - delivery orders are driven by the delivery workflow; the
  // generic release/close edges are hidden here (the server rejects them too).
  const isDelivery = tx.fulfillmentMethod === 'DELIVERY';

  // Phase 2D - the release edge never fires from a plain workflow button;
  // it opens the confirmation modal instead (single step, not multi-step).
  const canRelease =
    !isDelivery && tx.status === 'READY' && actions.some((rule) => rule.to === 'RELEASED');
  const workflowActions = actions.filter(
    (rule) => rule.to !== 'RELEASED' && !(isDelivery && rule.to === 'DONE'),
  );

  // Phase 2F - next legal delivery step (mirrors the server-side matrix;
  // the server validates every edge again on submit).
  const nextDeliveryStep: DeliveryStatus | null =
    isDelivery && tx.deliveryStatus
      ? (DELIVERY_STATUS_SUCCESORS[tx.deliveryStatus][0] ?? null)
      : null;
  const deliveryIndex = tx.deliveryStatus ? DELIVERY_STATUSES.indexOf(tx.deliveryStatus) : -1;
  const latestActual = tx.processing.reduce<number | null>(
    (last, step) => (step.endedAt && step.actualCount != null ? step.actualCount : last),
    null,
  );

  async function advance(to: TransactionStatus, note?: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const res = await api.patch<{ transaction: TransactionDetailDto }>(
        `/transactions/${tx.id}/status`,
        note ? { status: to, note } : { status: to },
      );
      onChanged(res.transaction);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the status.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function confirmRelease() {
    const ok = await advance('RELEASED', releaseNote.trim() || undefined);
    if (ok) {
      setConfirmingRelease(false);
      setReleaseNote('');
    }
  }

  // Phase 2F - one delivery step through the single server write path.
  async function confirmDeliveryStep(to: DeliveryStatus): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = { to };
      if (to === 'ASSIGNED') payload.assignee = assignee.trim();
      if (to === 'OUT_FOR_DELIVERY') {
        payload.deliveryMethod = deliveryMethod;
        if (handoffCount.trim()) payload.itemCountAtHandoff = Number(handoffCount.trim());
      }
      if (deliveryNote.trim()) payload.notes = deliveryNote.trim();
      const res = await api.patch<{ transaction: TransactionDetailDto }>(
        `/delivery/${tx.id}/status`,
        payload,
      );
      onChanged(res.transaction);
      setDeliveryTarget(null);
      setAssignee('');
      setHandoffCount('');
      setDeliveryNote('');
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the delivery.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const currentIndex = TRANSACTION_STATUSES.indexOf(tx.status);

  // Phase 2D - facts shown in the release confirmation (review before release).
  const releaseRows: { label: string; value: string }[] = tx.release
    ? [
        {
          label: 'Released by',
          value: `${tx.release.releasedByName} (${
            ROLE_LABELS[tx.release.releasedByRole] ?? tx.release.releasedByRole
          })`,
        },
        { label: 'Released at', value: formatDateTime(tx.release.createdAt) },
        { label: 'Handoff', value: tx.release.method === 'DELIVERY' ? 'Delivery' : 'Customer pickup' },
        { label: 'Items at release', value: String(tx.release.itemCount) },
        {
          label: 'Payment at release',
          value: `${tx.release.paymentStatus} · ${centsToPesos(tx.release.amountPaidCents)} of ${centsToPesos(tx.release.totalCents)}`,
        },
      ]
    : [];

  const facts: { label: string; value: string }[] = [
    { label: 'Customer', value: tx.customerName },
    { label: 'Contact', value: tx.customerPhone ?? '—' },
    {
      label: 'Service',
      value: `${tx.service.name} @ ${centsToPesos(tx.service.unitPriceCents)}${
        tx.service.unit === 'KG' ? '/kg' : tx.service.unit === 'PIECE' ? '/pc' : ''
      }`,
    },
    { label: 'Items', value: String(tx.itemCount) },
    ...(tx.weightGrams ? [{ label: 'Weight', value: `${(tx.weightGrams / 1000).toString()} kg` }] : []),
    { label: 'Total', value: centsToPesos(tx.totalCents) },
    { label: 'Paid', value: `${centsToPesos(tx.amountPaidCents)} · ${tx.paymentMethod ?? '—'}` },
    { label: 'Balance', value: centsToPesos(tx.totalCents - tx.amountPaidCents) },
    {
      label: 'Fulfillment',
      value:
        tx.fulfillmentMethod === 'DELIVERY'
          ? `Delivery${tx.deliveryAddress ? ` — ${tx.deliveryAddress}` : ''}`
          : 'Pickup',
    },
    { label: 'Expected', value: tx.expectedAt ? formatDateTime(tx.expectedAt) : '—' },
    { label: 'Received by', value: tx.createdBy?.name ?? '—' },
    { label: 'Received', value: formatDateTime(tx.createdAt) },
    { label: 'Last update', value: formatDateTime(tx.updatedAt) },
  ];

  return (
    <Modal title={`${tx.number} · ${tx.branch.code}`} onClose={onClose}>
      <div className="space-y-5">
        {error && <ErrorBanner message={error} />}

        {/* Current status + workflow progress */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={tx.status} />
            <DeliveryStatusBadge status={tx.deliveryStatus} />
            <Badge
              tone={
                tx.paymentStatus === 'PAID' ? 'emerald' : tx.paymentStatus === 'PARTIAL' ? 'amber' : 'rose'
              }
            >
              {tx.paymentStatus}
            </Badge>
            <Badge>{tx.branch.code}</Badge>
          </div>
          <ol className="mt-3 flex flex-wrap gap-1.5" aria-label="Workflow progress">
            {TRANSACTION_STATUSES.map((status, index) => (
              <li
                key={status}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                  index < currentIndex
                    ? 'bg-slate-100 text-slate-500 ring-slate-200'
                    : index === currentIndex
                      ? 'bg-indigo-600 text-accent-ink ring-indigo-600'
                      : 'bg-panel text-slate-400 ring-slate-200'
                }`}
              >
                {TRANSACTION_STATUS_LABELS[status]}
              </li>
            ))}
          </ol>
        </div>

        {/* Transitions this role may perform right now. The release edge is
            excluded here - it lives in the confirmed release panel below. */}
        {(workflowActions.length > 0 || !canRelease) && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <h3 className="text-sm font-semibold text-slate-700">Status workflow</h3>
            {workflowActions.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">
                {tx.status === 'DONE'
                  ? 'This transaction is closed.'
                  : isDelivery
                    ? 'This order is driven by the delivery workflow below.'
                    : 'No transitions available to your role.'}
              </p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {workflowActions.map((rule) => (
                  <Button key={rule.to} size="sm" disabled={busy} onClick={() => void advance(rule.to)}>
                    {rule.label}
                    <Icon name="check" className="h-4 w-4" />
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Phase 2F - delivery workflow: next legal step + append-only records.
            The server re-validates every edge; this panel only mirrors it. */}
        {isDelivery && tx.deliveryStatus && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Icon name="delivery" className="h-5 w-5 text-indigo-600" />
              <h3 className="text-sm font-semibold text-indigo-900">Delivery workflow</h3>
              <DeliveryStatusBadge status={tx.deliveryStatus} />
              {tx.deliveryAssignee && <Badge>{tx.deliveryAssignee}</Badge>}
            </div>

            <ol className="mt-3 flex flex-wrap gap-1.5" aria-label="Delivery progress">
              {DELIVERY_STATUSES.map((status, index) => (
                <li
                  key={status}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                    index < deliveryIndex
                      ? 'bg-slate-100 text-slate-500 ring-slate-200'
                      : index === deliveryIndex
                        ? 'bg-indigo-600 text-accent-ink ring-indigo-600'
                        : 'bg-panel text-slate-400 ring-slate-200'
                  }`}
                >
                  {DELIVERY_STATUS_LABELS[status]}
                </li>
              ))}
            </ol>

            {nextDeliveryStep ? (
              <Button
                className="mt-3"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setDeliveryTarget(nextDeliveryStep);
                }}
              >
                {DELIVERY_STEP_ACTIONS[nextDeliveryStep]}
                <Icon name="delivery" className="h-4 w-4" />
              </Button>
            ) : (
              <p className="mt-3 text-sm font-medium text-indigo-700">
                Delivery completed
                {tx.deliveryClosedAt ? ` · ${formatDateTime(tx.deliveryClosedAt)}` : ''}
              </p>
            )}

            {/* Append-only delivery history (one record per action). */}
            {tx.deliveryHandoffs.length > 0 && (
              <ol className="mt-3 space-y-2">
                {tx.deliveryHandoffs.map((record) => (
                  <li
                    key={record.id}
                    className="rounded-lg border border-indigo-100 bg-panel px-3 py-2 text-sm"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800">
                        {DELIVERY_STATUS_LABELS[record.toStatus]}
                      </span>
                      {record.deliveryMethod && <Badge>{record.deliveryMethod}</Badge>}
                      {record.itemCountAtHandoff != null && (
                        <span className="ml-auto rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                          {record.itemCountAtHandoff} items
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {record.actorName} ({ROLE_LABELS[record.actorRole] ?? record.actorRole}) ·{' '}
                      {formatDateTime(record.createdAt)}
                      {record.assignee ? ` · assignee ${record.assignee}` : ''}
                    </p>
                    {record.notes && (
                      <p className="mt-0.5 text-xs text-slate-500">“{record.notes}”</p>
                    )}
                  </li>
                ))}
              </ol>
            )}

            <p className="mt-2 text-xs text-slate-500">
              Informational only — delivery never changes payment state.
            </p>
          </div>
        )}

        {/* Phase 2D - READY FOR PICKUP: review everything on this panel, then
            release through a deliberate confirmation (server re-validates). */}
        {canRelease && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Icon name="pickup" className="h-5 w-5 text-emerald-600" />
              <h3 className="text-sm font-semibold text-emerald-900">Ready for pickup</h3>
              <Badge tone="emerald">READY FOR PICKUP</Badge>
            </div>
            <p className="mt-1 text-sm text-emerald-700">
              Review payment, item counts and processing history above, then release the laundry to
              the customer.
            </p>
            <Button
              className="mt-3"
              disabled={busy}
              onClick={() => {
                setError(null);
                setConfirmingRelease(true);
              }}
            >
              Release to customer
              <Icon name="pickup" className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Core facts */}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          {facts.map((row) => (
            <div key={row.label} className="flex justify-between gap-3 border-b border-slate-100 pb-2">
              <dt className="text-slate-500">{row.label}</dt>
              <dd className="text-right font-medium text-slate-900">{row.value}</dd>
            </div>
          ))}
        </dl>

        {/* Phase 2D - immutable release record: WHO released it, WHEN, HOW,
            with the item/payment snapshot taken at the moment of handoff. */}
        {tx.release && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Icon name="pickup" className="h-4 w-4 text-indigo-600" />
              <h3 className="text-sm font-semibold text-indigo-900">Release record</h3>
              <Badge tone="indigo">RELEASED</Badge>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {releaseRows.map((row) => (
                <div key={row.label} className="flex justify-between gap-3 border-b border-indigo-100 pb-1.5">
                  <dt className="text-slate-500">{row.label}</dt>
                  <dd className="text-right font-medium text-slate-900">{row.value}</dd>
                </div>
              ))}
            </dl>
            {tx.release.notes && (
              <p className="mt-2 text-sm text-slate-600">“{tx.release.notes}”</p>
            )}
          </div>
        )}

        {/* Phase 2C processing history: counts, machines, discrepancies (append-only) */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Processing history</h3>
          {tx.processing.length === 0 ? (
            <p className="mt-1 text-sm text-slate-500">No processing steps recorded yet.</p>
          ) : (
            <ol className="mt-2 space-y-2">
              {tx.processing.map((step) => (
                <li
                  key={step.id}
                  className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-800">
                      {PROCESSING_STAGE_LABELS[step.stage]}
                    </span>
                    {step.machine && <Badge>{step.machine}</Badge>}
                    {step.endedAt ? (
                      <span
                        className={`ml-auto rounded-full px-2 py-0.5 text-xs font-semibold ${
                          (step.difference ?? 0) === 0
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-rose-50 text-rose-700'
                        }`}
                      >
                        {step.expectedCount} → {step.actualCount} (Δ{' '}
                        {step.difference! > 0 ? '+' : ''}
                        {step.difference})
                      </span>
                    ) : (
                      <span className="ml-auto text-xs font-semibold text-indigo-600">
                        in progress
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {step.employeeName} ({ROLE_LABELS[step.employeeRole] ?? step.employeeRole}) ·{' '}
                    started {formatDateTime(step.startedAt)}
                    {step.endedAt ? ` → done ${formatDateTime(step.endedAt)}` : ''}
                  </p>
                  {step.note && <p className="mt-0.5 text-xs text-slate-500">“{step.note}”</p>}
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Status history timeline (append-only, server-written) */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Status history</h3>
          <ol className="mt-2 space-y-2">
            {tx.statusHistory.map((entry, index) => (
              <li
                key={entry.id}
                className="relative flex gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
              >
                <span
                  className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                    index === tx.statusHistory.length - 1 ? 'bg-indigo-500' : 'bg-slate-300'
                  }`}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800">
                    {entry.fromStatus
                      ? `${TRANSACTION_STATUS_LABELS[entry.fromStatus]} → ${TRANSACTION_STATUS_LABELS[entry.toStatus]}`
                      : `Received · ${TRANSACTION_STATUS_LABELS[entry.toStatus]}`}
                  </p>
                  <p className="text-xs text-slate-500">
                    {entry.actorName} ({ROLE_LABELS[entry.actorRole] ?? entry.actorRole}) ·{' '}
                    {formatDateTime(entry.createdAt)}
                  </p>
                  {entry.note && <p className="mt-0.5 text-xs text-slate-500">{entry.note}</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Phase 2D - customer pickup confirmation: one deliberate step that
            shows exactly which transaction/branch/customer/amount/payment is
            about to be released before the server is asked. */}
        {confirmingRelease && (
          <Modal
            title="Release this laundry to the customer?"
            onClose={() => {
              if (!busy) {
                setConfirmingRelease(false);
                setError(null);
              }
            }}
            footer={
              <>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    setConfirmingRelease(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button disabled={busy} onClick={() => void confirmRelease()}>
                  <Icon name="check" className="h-4 w-4" />
                  {busy ? 'Releasing…' : 'Yes, release it'}
                </Button>
              </>
            }
          >
            <div className="space-y-3">
              {error && <ErrorBanner message={error} />}
              <dl className="space-y-1.5 text-sm">
                {[
                  { label: 'Transaction', value: tx.number },
                  { label: 'Customer', value: tx.customerName },
                  { label: 'Items', value: String(tx.itemCount) },
                  { label: 'Amount', value: centsToPesos(tx.totalCents) },
                  { label: 'Branch', value: tx.branch.code },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between gap-3 border-b border-slate-100 pb-1.5"
                  >
                    <dt className="text-slate-500">{row.label}</dt>
                    <dd className="font-medium text-slate-900">{row.value}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-3 pb-1.5">
                  <dt className="text-slate-500">Payment</dt>
                  <dd className="flex items-center gap-2 font-medium text-slate-900">
                    <Badge
                      tone={
                        tx.paymentStatus === 'PAID'
                          ? 'emerald'
                          : tx.paymentStatus === 'PARTIAL'
                            ? 'amber'
                            : 'rose'
                      }
                    >
                      {tx.paymentStatus}
                    </Badge>
                    {tx.paymentStatus !== 'PAID' && (
                      <span className="text-xs font-semibold text-amber-700">
                        balance {centsToPesos(tx.totalCents - tx.amountPaidCents)}
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-slate-500">
                This hands the laundry to the customer. Payment state is recorded as-is — releasing
                does not mark the transaction paid.
              </p>
              <div>
                <label
                  htmlFor="release-note"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Release notes (optional)
                </label>
                <input
                  id="release-note"
                  value={releaseNote}
                  onChange={(e) => setReleaseNote(e.target.value)}
                  maxLength={300}
                  placeholder="e.g. claimed by a relative"
                  className={inputCls}
                />
              </div>
            </div>
          </Modal>
        )}

        {/* Phase 2F - delivery step confirmation: one deliberate step showing
            exactly what is about to happen (server re-validates everything). */}
        {deliveryTarget && (
          <Modal
            title={`${DELIVERY_STEP_ACTIONS[deliveryTarget]}?`}
            onClose={() => {
              if (!busy) {
                setDeliveryTarget(null);
                setError(null);
              }
            }}
            footer={
              <>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => {
                    setDeliveryTarget(null);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disabled={busy || (deliveryTarget === 'ASSIGNED' && !assignee.trim())}
                  onClick={() => void confirmDeliveryStep(deliveryTarget)}
                >
                  <Icon name="check" className="h-4 w-4" />
                  {busy ? 'Saving…' : 'Confirm'}
                </Button>
              </>
            }
          >
            <div className="space-y-3">
              {error && <ErrorBanner message={error} />}

              {deliveryTarget === 'ASSIGNED' && (
                <div>
                  <label
                    htmlFor="delivery-assignee"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    Assignee / driver
                  </label>
                  <input
                    id="delivery-assignee"
                    value={assignee}
                    onChange={(e) => setAssignee(e.target.value)}
                    maxLength={120}
                    placeholder="e.g. Juan Dela Cruz"
                    className={inputCls}
                  />
                </div>
              )}

              {deliveryTarget === 'OUT_FOR_DELIVERY' && (
                <>
                  <div>
                    <label
                      htmlFor="delivery-method"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Delivery method
                    </label>
                    <select
                      id="delivery-method"
                      value={deliveryMethod}
                      onChange={(e) => setDeliveryMethod(e.target.value as 'DRIVER' | 'OTHER')}
                      className={inputCls}
                    >
                      <option value="DRIVER">In-house driver</option>
                      <option value="OTHER">Other (courier / customer pick-up)</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="delivery-count"
                      className="mb-1.5 block text-sm font-medium text-slate-700"
                    >
                      Items handed over
                    </label>
                    <input
                      id="delivery-count"
                      type="number"
                      min={1}
                      max={10000}
                      value={handoffCount}
                      onChange={(e) => setHandoffCount(e.target.value)}
                      placeholder={String(latestActual ?? tx.itemCount)}
                      className={inputCls}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Defaults to the last counted amount ({latestActual ?? tx.itemCount}).
                      Snapshot only — earlier records are never rewritten.
                    </p>
                  </div>
                </>
              )}

              <div>
                <label
                  htmlFor="delivery-note"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Notes (optional)
                </label>
                <input
                  id="delivery-note"
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  maxLength={300}
                  placeholder="e.g. left at door, courier ref"
                  className={inputCls}
                />
              </div>

              <dl className="space-y-1.5 text-sm">
                {[
                  { label: 'Transaction', value: tx.number },
                  { label: 'Customer', value: tx.customerName },
                  { label: 'Branch', value: tx.branch.code },
                  { label: 'Items', value: String(tx.itemCount) },
                  { label: 'Amount', value: centsToPesos(tx.totalCents) },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between gap-3 border-b border-slate-100 pb-1.5"
                  >
                    <dt className="text-slate-500">{row.label}</dt>
                    <dd className="font-medium text-slate-900">{row.value}</dd>
                  </div>
                ))}
                <div className="flex justify-between gap-3 pb-1.5">
                  <dt className="text-slate-500">Payment</dt>
                  <dd className="flex items-center gap-2 font-medium text-slate-900">
                    <Badge
                      tone={
                        tx.paymentStatus === 'PAID'
                          ? 'emerald'
                          : tx.paymentStatus === 'PARTIAL'
                            ? 'amber'
                            : 'rose'
                      }
                    >
                      {tx.paymentStatus}
                    </Badge>
                    {tx.paymentStatus !== 'PAID' && (
                      <span className="text-xs font-semibold text-amber-700">
                        balance {centsToPesos(tx.totalCents - tx.amountPaidCents)}
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
              <p className="text-xs text-slate-500">
                Payment state is recorded as-is — this delivery step does not mark the
                transaction paid.
              </p>
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  );
}

/* ---------------- monitoring list ---------------- */

const autoWidthCls = inputCls.replace('w-full', '');

export function MonitoringPage() {
  const { branchFilter } = useAuth();
  const [items, setItems] = useState<TransactionDto[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<TransactionStatus, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | TransactionStatus>('ALL');
  // Phase 2G.6 - deep links from the payments report: ?q=<reference> prefills
  // the search and ?detail=<id> opens the existing transaction detail modal.
  const [searchParams] = useSearchParams();
  const deepLinkQuery = searchParams.get('q') ?? '';
  const [search, setSearch] = useState(deepLinkQuery);
  const [query, setQuery] = useState(deepLinkQuery.trim());
  const [detailId, setDetailId] = useState<string | null>(searchParams.get('detail'));
  const [detail, setDetail] = useState<TransactionDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounced search (same behaviour as the Transactions page).
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  // List: oldest -> newest by default, server-enforced.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (statusFilter !== 'ALL') params.set('status', statusFilter);
    if (query) params.set('q', query);

    void api
      .get<{ items: TransactionDto[]; statusCounts: Record<TransactionStatus, number> }>(
        `/transactions?${params.toString()}`,
      )
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setStatusCounts(res.statusCounts);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load transactions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [branchFilter, statusFilter, query, refreshKey]);

  // Detail (full breakdown + status history) whenever a row is opened.
  useEffect(() => {
    if (!detailId) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);
    void api
      .get<{ transaction: TransactionDetailDto }>(`/transactions/${detailId}`)
      .then((res) => {
        if (!cancelled) setDetail(res.transaction);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetailError(err instanceof ApiError ? err.message : 'Failed to load the transaction.');
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailId, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // "All" bucket = the whole scope, independent of the active status filter.
  const scopeTotal = statusCounts
    ? TRANSACTION_STATUSES.reduce((sum, status) => sum + statusCounts[status], 0)
    : items.length;

  const scopeLabel = branchFilter === 'ALL' ? 'All branches' : `Branch ${branchFilter}`;

  return (
    <section className="space-y-4" aria-label="Transaction monitoring">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Transaction Monitoring</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            {scopeLabel} · oldest first, with the full status history of every order.
          </p>
        </div>
        <Button variant="secondary" onClick={refresh}>
          <Icon name="refresh" className="h-5 w-5" />
          Refresh
        </Button>
      </div>

      {error && <ErrorBanner message={error} />}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-55 flex-1">
            <Icon
              name="search"
              className="pointer-events-none absolute top-1/2 left-3 h-5 w-5 -translate-y-1/2 text-slate-400"
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search number or customer…"
              aria-label="Search transactions"
              className={`${inputCls} pl-10`}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'ALL' | TransactionStatus)}
            aria-label="Filter by status"
            className={`${autoWidthCls} w-auto`}
          >
            <option value="ALL">All statuses</option>
            {TRANSACTION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {TRANSACTION_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>

        {/* Status chips with live counts */}
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Status filters">
          <button
            type="button"
            onClick={() => setStatusFilter('ALL')}
            className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${
              statusFilter === 'ALL'
                ? 'bg-accent text-accent-ink ring-accent'
                : 'bg-panel text-slate-600 ring-slate-300 hover:bg-slate-100'
            }`}
          >
            All · {scopeTotal}
          </button>
          {TRANSACTION_STATUSES.map((status) => {
            const active = statusFilter === status;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setStatusFilter(active ? 'ALL' : status)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset transition-colors ${
                  active
                    ? 'bg-indigo-600 text-accent-ink ring-indigo-600'
                    : 'bg-panel text-slate-600 ring-slate-300 hover:bg-slate-100'
                }`}
              >
                {TRANSACTION_STATUS_LABELS[status]} · {statusCounts ? statusCounts[status] : 0}
              </button>
            );
          })}
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-500">
          <Spinner className="h-5 w-5 text-indigo-600" /> Loading transactions…
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center p-8 text-center">
          <Icon name="monitoring" className="h-8 w-8 text-slate-300" />
          <p className="mt-2 font-medium text-slate-700">No matching transactions</p>
          <p className="mt-1 text-sm text-slate-500">Adjust the status filter or search to see more.</p>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-panel shadow-sm">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <tr>
                  <th className="px-4 py-3">Number</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Branch</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((tx) => (
                  <tr
                    key={tx.id}
                    onClick={() => setDetailId(tx.id)}
                    className="cursor-pointer hover:bg-slate-100/70"
                  >
                    <td className="px-4 py-3 font-semibold text-slate-900">{tx.number}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{tx.customerName}</p>
                      <p className="text-xs text-slate-500">{tx.service.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <Badge>{tx.branch.code}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{tx.itemCount}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {centsToPesos(tx.totalCents)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={tx.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(tx.createdAt).toLocaleDateString('en-PH')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </>
      )}

      {/* Detail: loading / error / full modal */}
      {detailId && !detail && !detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <div className="flex items-center justify-center gap-3 py-8 text-slate-500">
            <Spinner className="h-5 w-5 text-indigo-600" /> Loading transaction…
          </div>
        </Modal>
      )}
      {detailId && detailError && (
        <Modal title="Transaction" onClose={() => setDetailId(null)}>
          <ErrorBanner message={detailError} />
        </Modal>
      )}
      {detail && (
        <MonitoringDetailModal
          tx={detail}
          onClose={() => setDetailId(null)}
          onChanged={(next) => {
            setDetail(next);
            refresh();
          }}
        />
      )}
    </section>
  );
}
